import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowUpRight, ArrowRight, ChevronRight, Sparkles, ShieldCheck, BookOpen,
  Search, Map, Hammer, ClipboardCheck, Rocket, TrendingUp, Crown,
  Target, Layers, Compass, HeartHandshake, Award,
} from "lucide-react";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const METHOD = [
  { n: "01", key: "discover", title: "Discover", tagline: "Understand before we advise.",         icon: Search },
  { n: "02", key: "plan",     title: "Plan",     tagline: "Create the roadmap.",                  icon: Map },
  { n: "03", key: "build",    title: "Build",    tagline: "Build the business, not just paperwork.", icon: Hammer },
  { n: "04", key: "register", title: "Register", tagline: "Navigate registration with confidence.", icon: ClipboardCheck },
  { n: "05", key: "launch",   title: "Launch",   tagline: "Turn approval into a thriving business.", icon: Rocket },
  { n: "06", key: "protect",  title: "Protect",  tagline: "Stay compliant. Stay confident.",      icon: ShieldCheck },
  { n: "07", key: "grow",     title: "Grow",     tagline: "Build a stronger organisation.",        icon: TrendingUp },
  { n: "08", key: "lead",     title: "Lead",     tagline: "Create a lasting impact.",              icon: Crown },
];

const DIFFERENCE = [
  { icon: Target,        t: "Strategy First",        d: "We design your business before we prepare your registration." },
  { icon: Layers,        t: "Tailored Solutions",    d: "Every provider is different. Every solution should be too." },
  { icon: TrendingUp,    t: "Built for Growth",      d: "Systems designed to support where your business is going, not just where it is today." },
  { icon: Compass,       t: "Practical Compliance",  d: "Compliance should strengthen your business, not slow it down." },
  { icon: HeartHandshake,t: "Long Term Partnership", d: "Registration is just the beginning. We are here for the entire journey." },
  { icon: Award,         t: "Excellence by Design",  d: "Every process, document and recommendation is built to a professional standard." },
];

const INDUSTRIES = [
  { title: "Support coordination",    desc: "New entrants and growing teams ready to register or expand." },
  { title: "Allied health",           desc: "Therapists transitioning into the NDIS as registered providers." },
  { title: "SIL or SDA providers",    desc: "Complex, high risk groups requiring tight compliance frameworks." },
  { title: "Community participation", desc: "Day programs, group activities and capacity building services." },
  { title: "Personal care & domestic",desc: "Daily living supports and household tasks under NDIS Standards." },
  { title: "Plan management",         desc: "Plan managers requiring tailored financial compliance setups." },
];

const TESTIMONIALS = [
  { quote: "Elevate took us from confused to certified in 11 weeks. Their audit support was the difference.", who: "Operations Lead", org: "Sydney based SIL provider" },
  { quote: "Every policy was tailored to how we actually run. No template dumps, real partnership.",          who: "Director",        org: "Allied health practice" },
  { quote: "We failed our first audit attempt elsewhere. Elevate rebuilt the system and we passed cleanly.", who: "CEO",              org: "Plan management firm" },
];

const GUIDES = [
  { slug: "ndis-registration-checklist", title: "The Complete NDIS Registration Checklist", description: "A step by step roadmap covering documentation, evidence and audit readiness." },
  { slug: "compliance-essentials",       title: "NDIS Compliance Essentials Handbook",      description: "Stay compliant after registration with this evergreen operational reference." },
];

export default function Home() {
  const [modal, setModal] = useState({ open: false, guide: null });
  const openLead = (guide) => setModal({ open: true, guide });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-navy text-canvas overflow-hidden grain">
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
          <img src="https://images.pexels.com/photos/36765732/pexels-photo-36765732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy/90 to-teal/20" />
        </div>
        <svg className="absolute -right-32 top-0 w-[820px] h-[820px] opacity-30 pointer-events-none" viewBox="0 0 600 600" fill="none" aria-hidden>
          <defs>
            <linearGradient id="swoosh" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#163A66" />
              <stop offset="55%"  stopColor="#1F9AAE" />
              <stop offset="100%" stopColor="#5FD9E0" />
            </linearGradient>
          </defs>
          <path d="M 60 480 C 200 380, 320 280, 540 120" stroke="url(#swoosh)" strokeWidth="6" strokeLinecap="round" />
          <path d="M 100 520 C 240 420, 360 320, 580 160" stroke="url(#swoosh)" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        </svg>
        <div className="relative container-pad pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 animate-fade-up">
              <div className="overline">Australia's Provider Success Consultancy</div>
              <h1 className="font-display font-medium text-[42px] sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight mt-6">
                We don't build <span className="line-through opacity-50">registrations</span>. <br />
                We build <span className="text-brand-gradient font-serif-mark italic">successful providers</span>.
              </h1>
              <p className="mt-8 text-lg text-canvas/85 max-w-2xl leading-relaxed">
                Elevate Consulting is a consultancy company providing expert support and guidance with the auditing
                process for NDIS registration. We walk providers through every stage, from the first application to
                long term compliance and growth.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/method" data-testid="hero-method-cta" className="btn-ochre">
                  Discover The Elevate Method™ <ArrowUpRight size={18} />
                </Link>
                <Link to="/contact" data-testid="hero-book-cta" className="btn-light-on-navy">
                  Book a free Discovery
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-3">
              {[
                { k: "8 stages",  v: "The Elevate Method™" },
                { k: "End to end",v: "Full lifecycle support" },
                { k: "98%",       v: "Audit readiness" },
                { k: "1:1",       v: "Senior consultant" },
              ].map((s) => (
                <div key={s.v} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                  <div className="font-display text-xl text-teal-400 leading-tight">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-canvas/70 mt-3">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-sand border-b border-hairline">
        <div className="container-pad py-7 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-muted2">
          <span>NDIS Commission framework aligned</span>
          <span className="text-teal">·</span>
          <span>Independent audit ready</span>
          <span className="text-teal">·</span>
          <span>ISO informed policies</span>
          <span className="text-teal">·</span>
          <span>Servicing all states & territories</span>
        </div>
      </section>

      {/* THE ELEVATE METHOD */}
      <section className="container-pad py-24 sm:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="overline">The Elevate Method™</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
              A proprietary framework. <br />
              Every client. Every stage. <span className="text-brand-gradient font-serif-mark italic">From first idea to lasting leadership.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-muted2 leading-relaxed">
              The Elevate Method™ is our eight stage methodology for building successful disability service organisations.
              It's the backbone of every engagement, every conversation and every outcome.
            </p>
            <Link to="/method" data-testid="method-explore" className="mt-6 inline-flex items-center gap-2 text-navy font-medium hover:text-teal transition-colors">
              Explore the full method <ChevronRight size={16} />
            </Link>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {METHOD.map((m) => (
            <Link
              key={m.key}
              to="/method"
              data-testid={`method-tile-${m.key}`}
              className="card-soft p-5 group flex flex-col items-start"
            >
              <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center group-hover:bg-teal/20 transition-colors">
                <m.icon size={18} className="text-teal" />
              </div>
              <div className="font-display text-xs text-teal mt-4 tracking-widest">STAGE {m.n}</div>
              <div className="font-display text-lg text-navy mt-1">{m.title}</div>
              <div className="text-[11px] text-muted2 mt-1 leading-snug">{m.tagline}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* THE ELEVATE DIFFERENCE */}
      <section className="bg-sand py-24 sm:py-32">
        <div className="container-pad">
          <div className="overline">The Elevate Difference™</div>
          <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 max-w-3xl tracking-tight leading-[1.08]">
            Six commitments that define how we work.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {DIFFERENCE.map((d) => (
              <div key={d.t} className="card-soft p-8">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <d.icon size={22} className="text-teal" />
                </div>
                <div className="font-display text-xl text-navy mt-5">{d.t}</div>
                <p className="text-muted2 mt-3 text-sm leading-relaxed">{d.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="container-pad py-24 sm:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4 md:sticky md:top-28">
            <div className="overline">Who we work with</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
              Built for the providers shaping Australian disability support.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              From sole operators to multi site organisations. If you deliver services under the NDIS, we help you register
              and stay registered.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {INDUSTRIES.map((i) => (
              <div key={i.title} className="card-feature p-6">
                <div className="font-display text-lg text-navy">{i.title}</div>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">{i.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy text-canvas py-24 sm:py-32 grain relative overflow-hidden">
        <svg className="absolute -left-20 -bottom-20 w-[520px] h-[520px] opacity-25 pointer-events-none" viewBox="0 0 600 600" aria-hidden>
          <defs>
            <linearGradient id="swoosh2" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1F9AAE" />
              <stop offset="100%" stopColor="#5FD9E0" />
            </linearGradient>
          </defs>
          <path d="M 40 500 C 200 380, 360 260, 580 100" stroke="url(#swoosh2)" strokeWidth="5" strokeLinecap="round" />
        </svg>
        <div className="container-pad relative">
          <div className="overline">Voices from the field</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 max-w-3xl tracking-tight leading-[1.08]">
            What providers say after they're registered.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 min-h-[280px] flex flex-col" data-testid={`testimonial-${idx}`}>
                <Sparkles size={20} className="text-teal-400" />
                <p className="font-display text-lg leading-snug mt-6 flex-1">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="text-sm">{t.who}</div>
                  <div className="text-xs text-canvas/60">{t.org}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-canvas/50 mt-6">Testimonial placeholders, replace with verified client quotes when ready.</p>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section className="container-pad py-24 sm:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="overline">Free resources</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
              Practical NDIS guides, written by the people who run audits.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              Knowledge before documentation. Download the resources our consultants share with new clients.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {GUIDES.map((g) => (
              <button
                key={g.slug}
                data-testid={`download-guide-${g.slug}`}
                onClick={() => openLead(g)}
                className="card-soft p-8 text-left group min-h-[240px] flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <BookOpen size={22} className="text-teal" />
                </div>
                <div className="font-display text-xl text-navy mt-5">{g.title}</div>
                <p className="text-sm text-muted2 mt-2 leading-relaxed flex-1">{g.description}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-navy font-medium text-sm group-hover:text-teal transition-colors">
                  Get the guide <ArrowRight size={14} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad pb-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-10 sm:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative overflow-hidden grain">
          <div className="md:col-span-8 relative">
            <div className="overline" style={{ color: '#A7E0E6' }}>Free Discovery Workshop</div>
            <h3 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight leading-[1.1]">
              Every Elevate journey begins with Discover.
            </h3>
            <p className="text-canvas/85 mt-4 max-w-xl">
              30 minutes with a senior consultant. We'll map your provider pathway and tell you honestly whether we're the right partner.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end relative">
            <Link to="/contact" data-testid="cta-book-discovery" className="btn-light-on-navy">
              Book my call <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <LeadCaptureModal open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })} guide={modal.guide} />
    </div>
  );
}
